import { useQuery } from "@apollo/client";
import differenceWith from "lodash-es/differenceWith";
import { useMemo } from "react";

import { BaseSelectItem } from "ds/components/Select/types";
import { Context } from "types/generated";

import { GET_ATTACHABLE_CONTEXTS_IN_SPACE } from "./gql";

type AttachableContextOption = BaseSelectItem & {
  value: Context["name"];
};

type UseGetAttachableContextsProps = {
  spaceId: string;
  attachedContextIds: string[];
  skip?: boolean;
};

type UseGetAttachableContextsData = {
  contexts: AttachableContextOption[];
  loading: boolean;
};

const useGetAttachableContexts = ({
  spaceId,
  attachedContextIds,
}: UseGetAttachableContextsProps): UseGetAttachableContextsData => {
  const { data, loading } = useQuery<{ attachableContextsInSpace: Context[] }>(
    GET_ATTACHABLE_CONTEXTS_IN_SPACE,
    {
      variables: { id: spaceId },
    }
  );

  const contexts = useMemo(
    () =>
      differenceWith(
        (data?.attachableContextsInSpace || []).map((context) => ({
          value: context.id,
          label: context.name,
        })),
        attachedContextIds,
        (a, b) => a.value === b
      ),
    [data?.attachableContextsInSpace, attachedContextIds]
  );

  return {
    contexts,
    loading,
  };
};

export default useGetAttachableContexts;
